import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../api/client';
import type { Problem, SessionDetail, StudioEvent, StudioMessage, StudioQuestion } from '../api/types';
import { toProblem } from '../app/useAsync';

/** 1 回で取るメッセージの数（API の上限）。 */
const PAGE = 500;

export interface StudioSessionState {
  /** 最新のセッション。まだ無ければ null。 */
  detail: SessionDetail | null;
  /**
   * 購読の起点にする snapshot の cursor。取り直し（reload）のときだけ変わる。
   * detail.eventCursor は SSE を受けるたびの getSession で進むので、購読の依存に使うと 1 イベントごとに張り直しになる。
   */
  eventCursor: string | null;
  /** seq の昇順。 */
  messages: StudioMessage[];
  /**
   * これまでに見た質問（詳細の openQuestions と回答の応答）。回答済みの 1 行に質問文を出すため。
   * API に回答済みの質問を読む経路が無いので、ページを開き直す前の分しか持てない。
   */
  questions: StudioQuestion[];
  loading: boolean;
  problem: Problem | null;
  reload(): void;
  applyEvent(event: StudioEvent): void;
  setDetail(detail: SessionDetail): void;
  rememberQuestion(question: StudioQuestion): void;
}

/**
 * Studio の会話の snapshot と、SSE を受けたときの取り直し。
 * studio.message は after = min(手元の最後の seq, event.seq - 1) から取り直して id で置き換える（streaming の行は seq を変えずに本文が変わる。C27）。
 * studio.turn / question / proposal はセッションの詳細を取り直す。
 * studio.question の answered / superseded は、詳細に載らなくなる質問なので覚えている写しの状態も書き換える（C8）。
 */
export function useStudioSession({ api, workspaceId }: { api: ApiClient; workspaceId: string }): StudioSessionState {
  const [detail, setDetailState] = useState<SessionDetail | null>(null);
  const [eventCursor, setEventCursor] = useState<string | null>(null);
  const [messages, setMessages] = useState<StudioMessage[]>([]);
  const [questions, setQuestions] = useState<StudioQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  // 非同期の応答が、読み直した後の別セッションに混ざらないよう、いまのセッションと最後の seq を持つ。
  const sessionRef = useRef<string | null>(null);
  const lastSeqRef = useRef(0);

  const rememberQuestions = useCallback((items: readonly StudioQuestion[]) => {
    if (items.length === 0) return;
    setQuestions((current) => {
      const byId = new Map(current.map((question) => [question.id, question]));
      for (const item of items) byId.set(item.id, item);
      return [...byId.values()];
    });
  }, []);

  /**
   * 閉じた質問の状態を書き換える。詳細の openQuestions は open だけなので、
   * 覚えている写しは studio.question の answered / superseded でしか閉じられない（C8）。
   */
  const closeQuestion = useCallback((questionId: string, status: 'answered' | 'superseded') => {
    setQuestions((current) => {
      const index = current.findIndex((question) => question.id === questionId);
      const found = current[index];
      if (found === undefined || found.status === status) return current;
      const next = [...current];
      next[index] = { ...found, status };
      return next;
    });
  }, []);

  const mergeMessages = useCallback((items: readonly StudioMessage[]) => {
    if (items.length === 0) return;
    lastSeqRef.current = Math.max(lastSeqRef.current, ...items.map((message) => message.seq));
    setMessages((current) => {
      const byId = new Map(current.map((message) => [message.id, message]));
      for (const item of items) byId.set(item.id, item);
      return [...byId.values()].sort((left, right) => left.seq - right.seq);
    });
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadLatest(api, workspaceId)
      .then((result) => {
        if (!active) return;
        sessionRef.current = result.detail?.session.id ?? null;
        lastSeqRef.current = result.lastSeq;
        setDetailState(result.detail);
        setEventCursor(result.detail?.eventCursor ?? null);
        setMessages(result.messages);
        if (result.detail) rememberQuestions(result.detail.openQuestions);
        setProblem(null);
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setProblem(toProblem(error));
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, workspaceId, reloadToken, rememberQuestions]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const setDetail = useCallback(
    (next: SessionDetail) => {
      sessionRef.current = next.session.id;
      setDetailState(next);
      rememberQuestions(next.openQuestions);
    },
    [rememberQuestions],
  );

  const applyEvent = useCallback(
    (event: StudioEvent) => {
      if (!event.sessionId) return;
      const current = sessionRef.current;
      if (current !== event.sessionId) {
        // 別のタブや別のセッションで始まったターン。最新のセッションを読み直す。
        if (event.type === 'studio.turn') setReloadToken((token) => token + 1);
        return;
      }
      if (event.type === 'studio.message') {
        const seq = event.seq ?? lastSeqRef.current + 1;
        const after = Math.max(0, Math.min(lastSeqRef.current, seq - 1));
        api
          .listSessionMessages(workspaceId, current, { after, limit: PAGE })
          .then((list) => {
            if (sessionRef.current === current) mergeMessages(list.items);
          })
          .catch((error: unknown) => setProblem(toProblem(error)));
        return;
      }
      if (event.type === 'studio.question' && event.questionId !== undefined) {
        // 別の指示で取り下げられた質問（superseded）と、別のタブでの回答（answered）を覚えている写しに映す。
        if (event.action === 'superseded' || event.action === 'answered') closeQuestion(event.questionId, event.action);
      }
      if (event.type === 'studio.turn' || event.type === 'studio.question' || event.type === 'studio.proposal') {
        api
          .getSession(workspaceId, current)
          .then((loaded) => {
            if (sessionRef.current !== current) return;
            setDetailState(loaded);
            rememberQuestions(loaded.openQuestions);
          })
          .catch((error: unknown) => setProblem(toProblem(error)));
      }
    },
    [api, workspaceId, mergeMessages, rememberQuestions, closeQuestion],
  );

  const rememberQuestion = useCallback((question: StudioQuestion) => rememberQuestions([question]), [rememberQuestions]);

  return { detail, eventCursor, messages, questions, loading, problem, reload, applyEvent, setDetail, rememberQuestion };
}

async function loadLatest(
  api: ApiClient,
  workspaceId: string,
): Promise<{ detail: SessionDetail | null; messages: StudioMessage[]; lastSeq: number }> {
  const page = await api.listSessions(workspaceId, { limit: 1 });
  const latest = page.items[0];
  if (!latest) return { detail: null, messages: [], lastSeq: 0 };
  const detail = await api.getSession(workspaceId, latest.id);
  const messages: StudioMessage[] = [];
  let after = 0;
  let lastSeq = 0;
  for (;;) {
    const list = await api.listSessionMessages(workspaceId, latest.id, { after, limit: PAGE });
    messages.push(...list.items);
    lastSeq = list.lastSeq;
    const last = list.items.at(-1);
    if (list.items.length < PAGE || last === undefined) break;
    after = last.seq;
  }
  return { detail, messages, lastSeq };
}
